import * as React from 'react'
import { moment } from '../../../services/utils/moment.service'
import { I18nService } from '../../../services/utils/i18n.service'
import { changelogsGet } from '../../../services/models/changelogs.service'
import { ModalDecoration, ModalHeaderIcon } from '../../../services/utils/modals.service'
import { usePromise } from '../../../hooks/use-promise'
import { useLocalStorage } from '../../../hooks/use-local-storage'
import { ChangelogsResponse } from '../../../services/json-schemas/serializers/changelogs-response'
import styled from 'styled-components'

const SummaryContainer = styled.div`
  h5:not(:first-child) { margin-top: 1em; }
  font-size: 90%;
  line-height: 1.4em;
  margin-bottom: 15px;
  max-height: 35em;
  overflow-y: auto;
  text-align: justify;
`

const i18nScoped = new I18nService(require('./modal-changelog.component.yml'))

interface Props {
  force?: boolean
  onClose?: () => void
}

export default function ModalChangelogComponent ({ force, onClose }: Props) {
  const [openModal, setOpenModal] = React.useState(true)
  const [currentChangelogIndex, setCurrentChangelogIndex] = React.useState(0)
  const [lastCheckedDate, setLastCheckedDate] = useLocalStorage('modalChangelogComponent.lastCheckedDate', null)
  const [lastDisplayedId, setLastDisplayedId] = useLocalStorage('modalChangelogComponent.lastDisplayedId', '0' as string)

  const changelogs = usePromise<ChangelogsResponse['changelogs']>(async () => {
    if (force) return await changelogsGet().then(_ => _.changelogs)

    if (window.locale.startsWith('fr') && (!lastCheckedDate || moment().isAfter(new Date(lastCheckedDate), 'hour'))) {
      const logs = await changelogsGet().then(_ => _.changelogs)

      // handle both published_at and lastDisplayedId, because published_at could be antidated from a few hours when published from admin UI.
      if (logs.some(log => moment(log.published_at).isAfter(new Date(lastCheckedDate)))) return logs
      if (logs.some(log => log.id > +lastDisplayedId)) return logs

      setLastCheckedDate(new Date().toISOString())
      return []
    }

    return Promise.resolve([])
  }, [], { defaultState: [] }) // eslint-disable-line react-hooks/exhaustive-deps -- on mount only

  const onCloseModal = React.useCallback(() => {
    setOpenModal(false)
    setLastCheckedDate(new Date().toISOString())
    setLastDisplayedId(changelogs[0]?.id.toString())
    onClose?.()
  }, [changelogs, onClose, setLastDisplayedId, setLastCheckedDate])

  const currentChangelog = changelogs[currentChangelogIndex]
  if (!changelogs.length || !openModal || !currentChangelog) return null

  return <ModalDecoration 
    title={ currentChangelog.title }
    controls={{ onCancel: onCloseModal, onResult: onCloseModal }}
    options={{ iconElements: [
      currentChangelogIndex > 0 && <ModalHeaderIcon key="prev" className="icon-prev" onClick={ () => setCurrentChangelogIndex(currentChangelogIndex - 1) } />,
      currentChangelogIndex < changelogs.length - 1 && <ModalHeaderIcon key="next" className="icon-next" onClick={ () => setCurrentChangelogIndex(currentChangelogIndex + 1) } />
    ].filter(Boolean) }}
    contentGenerator={ ({ onCancel }) => <div className="modal-inner" style={{ 'maxHeight': '40em', 'overflow': 'auto' }}>
      <div style={{ 'marginBottom': '15px' }}>
        <SummaryContainer dangerouslySetInnerHTML={{ __html: currentChangelog.summary_html }} />
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between' }} className='u-ptm u-borderT'>
        { /* previous release notes are hosted on a retired website, some will be migrated manually */ }
        { !currentChangelog.detail_url.includes('ogustine.com') && <a
          className='buttonNeutral button--S'
          target="_blank"
          rel="noreferrer"
          href={ currentChangelog.detail_url }
          children={ i18nScoped.t('more_info') } /> }

        <button
          className='button button--S'
          onClick={ onCancel }
          children={ i18nScoped.t('close') } />
      </div>
    </div> }
 />
}